import { formatCents, tt } from '@agos/shared'

/**
 * A purchase-order draft, and whether it could actually be sent to a supplier.
 *
 * Auto-generation takes every item below its minimum and asks for the shortfall from
 * that item's default supplier. The arithmetic is right and the result is not
 * orderable: the two drafts in the restored data ask **LUNYS for 1 g of paprika** and
 * **Bidfood for 4,005 g of liquid cheddar** — `minQty − qty` in the stock item's own
 * unit, with nothing converting that into a purchase unit or a pack size.
 *
 * That is FB-082, and it is not something a frontend can fix: only the supplier's
 * catalogue knows that cheddar comes in 2 kg tubs. What the frontend can do is refuse
 * to present a gram-scale order as ready to send.
 *
 * FB-083 is the second half. **No supplier in the data has an e-mail address**, and
 * legacy renders its *Odoslať e-mailom* button only when one exists — so the page's
 * primary action is simply absent, with nothing saying why.
 */

export type DraftStatus = 'draft' | 'sent' | 'cancelled'

export interface DraftRow {
  id?: number
  supplierId?: number
  supplierName?: string | null
  status?: string
  notes?: string | null
  lineCount?: number
  /** Σ qty × estimated unit price. Zero when no line has a price. */
  estimatedTotal?: number
  createdAt?: string
  sentAt?: string | null
}

export interface DraftLine {
  id?: number
  stockItemId?: number
  stockItemName?: string | null
  supplierCode?: string | null
  qty?: number
  unit?: string
  /** A per-unit price, so it keeps its decimals (FB-056). */
  estimatedUnitPrice?: number | null
}

export interface DraftDetail extends DraftRow {
  supplierEmail?: string | null
  lines?: DraftLine[]
}

const STATUS_LABELS: Record<DraftStatus, string> = {
  draft: tt('Návrh'),
  sent: tt('Odoslané'),
  cancelled: tt('Zrušené'),
}

export type BadgeColor = 'zinc' | 'emerald' | 'red' | 'amber'

const STATUS_COLORS: Record<DraftStatus, BadgeColor> = {
  draft: 'zinc',
  sent: 'emerald',
  cancelled: 'red',
}

export function statusLabel(status: string | undefined): string {
  if (!status) return ''
  return STATUS_LABELS[status as DraftStatus] ?? status
}

export function statusColor(status: string | undefined): BadgeColor {
  return STATUS_COLORS[status as DraftStatus] ?? 'zinc'
}

/** Postgres prints `2026-07-29 08:15:04.379829+00`, which `Date.parse` refuses twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * A line's value: quantity times an estimated unit price.
 *
 * `null` rather than zero when there is no price — a draft worth nothing and a draft
 * of unknown worth are different things, and legacy shows both as `0,00 €`.
 */
export function lineValue(line: DraftLine): number | null {
  const price = line.estimatedUnitPrice
  if (price == null || !Number.isFinite(price) || price <= 0) return null
  const qty = line.qty ?? 0
  return Math.round(qty * price * 100) / 100
}

export interface DraftEstimate {
  /** The total over the lines that have a price. */
  totalValue: number
  pricedLines: number
  totalLines: number
  /** Whether every line could be priced. */
  complete: boolean
}

export function estimateOf(lines: DraftLine[]): DraftEstimate {
  const values = lines.map(lineValue)
  const priced = values.filter((value): value is number => value !== null)
  return {
    totalValue: Math.round(priced.reduce((sum, value) => sum + value, 0) * 100) / 100,
    pricedLines: priced.length,
    totalLines: lines.length,
    complete: lines.length > 0 && priced.length === lines.length,
  }
}

/**
 * Quantities below this, in a mass or volume unit, are not something a wholesaler
 * sells. A gram of paprika is a pinch.
 */
const IMPLAUSIBLE_BY_UNIT: Record<string, number> = {
  g: 100,
  ml: 100,
  kg: 0.1,
  l: 0.1,
}

export type LineProblem =
  | { kind: 'no-quantity' }
  /** A quantity too small to be a real order line — FB-082. */
  | { kind: 'implausible-quantity'; qty: number; unit: string; floor: number }
  /** No supplier code, so the supplier cannot tell what is being asked for. */
  | { kind: 'no-supplier-code' }

export function lineProblems(line: DraftLine): LineProblem[] {
  const problems: LineProblem[] = []
  const qty = line.qty ?? 0

  if (qty <= 0) {
    problems.push({ kind: 'no-quantity' })
  } else {
    const unit = (line.unit ?? '').toLowerCase()
    const floor = IMPLAUSIBLE_BY_UNIT[unit]
    if (floor !== undefined && qty < floor) {
      problems.push({ kind: 'implausible-quantity', qty, unit: line.unit ?? '', floor })
    }
  }

  if ((line.supplierCode ?? '').trim() === '') {
    problems.push({ kind: 'no-supplier-code' })
  }

  return problems
}

export type SendBlocker =
  | { kind: 'already-sent' }
  | { kind: 'cancelled' }
  | { kind: 'no-lines' }
  /** FB-083 — the supplier has no e-mail address on record. */
  | { kind: 'no-email' }
  /** FB-082 — at least one line asks for a quantity nobody sells. */
  | { kind: 'implausible-lines'; count: number }

/**
 * Why a draft cannot be sent, in the order that matters.
 *
 * Legacy hides its mail button when there is no address and shows nothing otherwise,
 * so a draft that cannot be sent looks identical to one nobody has got round to.
 * Every reason is named instead.
 */
export function sendBlockers(draft: DraftDetail): SendBlocker[] {
  const blockers: SendBlocker[] = []
  if (draft.status === 'sent') blockers.push({ kind: 'already-sent' })
  if (draft.status === 'cancelled') blockers.push({ kind: 'cancelled' })

  const lines = draft.lines ?? []
  if (lines.length === 0) blockers.push({ kind: 'no-lines' })
  if ((draft.supplierEmail ?? '').trim() === '') blockers.push({ kind: 'no-email' })

  const implausible = lines.filter((line) =>
    lineProblems(line).some((problem) => problem.kind === 'implausible-quantity'),
  ).length
  if (implausible > 0) blockers.push({ kind: 'implausible-lines', count: implausible })

  return blockers
}

/** Whether a mail client can be opened for this draft at all. */
export function canMail(draft: DraftDetail): boolean {
  return (draft.supplierEmail ?? '').trim() !== '' && (draft.lines ?? []).length > 0
}

/**
 * The order as plain text, for the body of an e-mail.
 *
 * Kept to what a supplier needs: their own code where there is one, the item name,
 * and the quantity with its unit. No prices — an estimate is ours, not an offer.
 */
export function mailBody(draft: DraftDetail): string {
  const lines = (draft.lines ?? []).map((line) => {
    const code = (line.supplierCode ?? '').trim()
    const name = line.stockItemName ?? ''
    const qty = formatQty(line.qty)
    return `${code ? `${code} - ` : ''}${name}: ${qty} ${line.unit ?? ''}`.trim()
  })

  return [
    `${tt('Dobrý deň,')}`,
    '',
    `${tt('prosíme o dodanie:')}`,
    ...lines,
    '',
    draft.notes ? `${draft.notes}` : '',
    `${tt('Ďakujeme.')}`,
  ]
    .filter((line, index, all) => line !== '' || all[index - 1] !== '')
    .join('\n')
}

export function mailSubject(draft: DraftDetail): string {
  return `${tt('Objednávka')} #${draft.id ?? ''}`
}

/** `4,005`, `1`, `2,5` — a quantity, never padded. */
export function formatQty(qty: number | null | undefined): string {
  const value = qty ?? 0
  const rounded = Math.round(value * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

/**
 * `12,50 €` — an estimate is money, so two decimals.
 *
 * Through the shared formatter since §9.3: the hand-rolled `toFixed(2).replace('.', ',')` got the comma
 * right and left out the thousands separator, so a four-figure estimate read `1234,50 €` on a screen where
 * every other four-figure amount reads `1 234,50 €`.
 */
export function formatMoney(value: number | null): string {
  if (value === null) return '-'
  return formatCents(Math.round(value * 100))
}

export function filterDrafts(drafts: DraftRow[], status: string): DraftRow[] {
  if (status === 'all') return drafts
  return drafts.filter((draft) => draft.status === status)
}

/** Newest first — a draft is a thing somebody is about to act on. */
export function byNewest(drafts: DraftRow[]): DraftRow[] {
  return [...drafts].sort(
    (left, right) => (parseStamp(right.createdAt) ?? 0) - (parseStamp(left.createdAt) ?? 0),
  )
}

/**
 * Whether this draft's lines may be edited at all — `BACKEND.md` §10A.21, `FRONTEND.md` §9.77.
 *
 * Only a draft. A `sent` one has been ordered from the supplier and its lines are the record of what
 * was ordered; a `cancelled` one is a decision not to order. The server refuses both, and the editor
 * is absent rather than present-and-rejected — a badge saying *Odoslané* beside editable boxes would
 * be an invitation the server exists to decline.
 */
export function linesAreEditable(draft: DraftDetail): boolean {
  return draft.status === 'draft'
}

/**
 * The quantity as typed, or null when it is not a number this can send — §9.77.
 *
 * Accepts the Slovak decimal comma, because that is what the keyboard gives and what every other
 * number on this screen is *shown* with. Rejects a negative: a reorder for minus five kilograms is
 * not a correction, and the server would take it literally.
 */
export function parsedQty(typed: string): number | null {
  const cleaned = typed.trim().replace(',', '.')
  if (cleaned === '') return null
  const value = Number(cleaned)
  return Number.isFinite(value) && value >= 0 ? value : null
}

/**
 * The lines with one quantity replaced — §9.77.
 *
 * A new array with a new line, rather than a mutation: the panel keeps its edits in state until
 * *Uložiť*, and React will not re-render a list whose objects it has already seen.
 */
export function withQty(lines: DraftLine[], index: number, qty: number): DraftLine[] {
  return lines.map((line, position) => (position === index ? { ...line, qty } : line))
}

/** The lines with one removed — the other half of what the wholesale PUT is for. */
export function withoutLine(lines: DraftLine[], index: number): DraftLine[] {
  return lines.filter((_, position) => position !== index)
}

/**
 * Whether the edited lines differ from what the server holds — §9.77.
 *
 * *Uložiť* is offered only when there is something to save, so a panel somebody merely opened does
 * not invite a write that would rewrite every line to the values it already has.
 *
 * Compared on what `linesPayload` actually sends, because that is what the save would change: a
 * difference in a field the payload omits is not a difference the server would see.
 */
export function linesChanged(edited: DraftLine[], original: DraftLine[]): boolean {
  return JSON.stringify(linesPayload(edited)) !== JSON.stringify(linesPayload(original))
}

/** The body `PUT /reorder-drafts/{id}/lines` wants. */
export function linesPayload(lines: DraftLine[]): Record<string, unknown> {
  return {
    lines: lines.map((line) => ({
      stockItemId: line.stockItemId,
      qty: line.qty ?? 0,
      unit: line.unit ?? '',
      supplierCode: (line.supplierCode ?? '').trim() || null,
      estimatedUnitPrice: line.estimatedUnitPrice ?? null,
    })),
  }
}
